import React, { FC, MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { Schema, InputPicker, Form as RsuiteForm, FlexboxGrid } from "rsuite";
import { FormInstance } from "rsuite/esm/Form/hooks/useFormRef";
import { FormProps } from "rsuite/esm/Form/Form";
import cn from "classnames";

import {
    CultureSettings,
    Currency,
} from "store/environments/tenants/culture-settings/culture-settings-types";
import "./Form.less";

interface Props extends FormProps {
    formRef: MutableRefObject<FormInstance<Record<string, any>> | null>;

    cultureSettings: CultureSettings;
    currencies: Currency[];
    supportedCultures: string[];
    cultures: string[];
}

const Form: FC<Props> = ({
    formRef,
    cultureSettings,
    currencies,
    supportedCultures,
    cultures,
    ...props
}: Props) => {
    const { t } = useTranslation();

    const { StringType, NumberType } = Schema.Types;

    const cultureData = supportedCultures.map((culture) => ({
        value: culture,
        label: culture,
    }));

    const numberAndDateData = cultures.map((culture) => ({
        value: culture,
        label: culture,
    }));

    const preciseScaleInputPickerData = Array.from(Array(9).keys()).map(
        (number) => {
            // Dropdown values should contain numbers 2 - 10
            const item = number + 2;

            return { value: item, label: item };
        }
    );

    const preciseQuantityData = Array.from(Array(11).keys()).map((number) => {
        // Dropdown values should contain numbers 0 - 10
        return { value: number, label: number };
    });

    const currencyData = currencies.map((currency) => ({
        value: currency.code,
        label: currency.code,
    }));

    const getFormattedDateTime = (
        numberAndDateCulture: string | null,
        options: Intl.DateTimeFormatOptions
    ): string =>
        numberAndDateCulture
            ? new Intl.DateTimeFormat(numberAndDateCulture, options).format(
                  Date.now()
              )
            : t("unknown");

    const getFormattedNumber = (scale: number | null): string => {
        return scale ? (0).toFixed(scale) : t("unknown");
    };

    const handleCleanField = (fieldName: formValueType) => {
        if (
            formRef &&
            formRef.current &&
            (cultureSettings?.[fieldName] || !cultureSettings?.[fieldName])
        ) {
            return formRef.current.cleanErrorForField(fieldName);
        }
    };

    const shortDateTime = getFormattedDateTime(
        cultureSettings.numberAndDateCulture,
        {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        }
    );
    const longDateTime = getFormattedDateTime(
        cultureSettings.numberAndDateCulture,
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
        }
    );

    const preciseScaleView = getFormattedNumber(cultureSettings.preciseScale);
    const quantityPreciseScaleView = getFormattedNumber(
        cultureSettings.quantityPreciseScale
    );

    const model = Schema.Model({
        culture: StringType().isRequired(
            t("tenantConfig.cultureSettingsPage.common.isRequired")
        ),
        numberAndDateCulture: StringType().isRequired(
            t("tenantConfig.cultureSettingsPage.common.isRequired")
        ),
        preciseScale: NumberType().isRequired(
            t("tenantConfig.cultureSettingsPage.common.isRequired")
        ),
        quantityPreciseScale: NumberType().isRequired(
            t("tenantConfig.cultureSettingsPage.common.isRequired")
        ),
        defaultCurrency: StringType().isRequired(
            t("tenantConfig.cultureSettingsPage.common.isRequired")
        ),
    });

    type formValueType = keyof CultureSettings;

    const formFieldKeys: Record<string, formValueType> = {
        culture: "culture",
        numberAndDate: "numberAndDateCulture",
        preciseScale: "preciseScale",
        quantityPreciseScale: "quantityPreciseScale",
        defaultCurrency: "defaultCurrency",
    };

    return (
        <>
            <RsuiteForm
                {...props}
                ref={formRef}
                model={model}
                formValue={cultureSettings}
            >
                <FlexboxGrid
                    justify={"start"}
                    align={"bottom"}
                    style={{ marginBottom: "1.5rem" }}
                >
                    <FlexboxGrid.Item style={{ marginRight: "2.5rem" }}>
                        <RsuiteForm.Group>
                            <RsuiteForm.ControlLabel className="regional_settings__form__label">
                                {t(
                                    "tenantConfig.cultureSettingsPage.culture.title"
                                )}
                                <RsuiteForm.HelpText
                                    className="regional_settings__form__help"
                                    tooltip
                                >
                                    {t(
                                        "tenantConfig.cultureSettingsPage.culture.tooltip"
                                    )}
                                </RsuiteForm.HelpText>
                            </RsuiteForm.ControlLabel>
                            <RsuiteForm.Control
                                accepter={InputPicker}
                                preventOverflow={true}
                                data={cultureData}
                                cleanable={false}
                                onClean={() =>
                                    handleCleanField(formFieldKeys.culture)
                                }
                                name={formFieldKeys.culture}
                                className={cn(
                                    "regional_settings__form__control",
                                    "vertically-aligned-picker"
                                )}
                            />
                        </RsuiteForm.Group>
                    </FlexboxGrid.Item>
                </FlexboxGrid>

                <FlexboxGrid
                    justify={"start"}
                    align={"bottom"}
                    style={{ marginBottom: "1.5rem", rowGap: "1.5rem" }}
                >
                    <FlexboxGrid.Item style={{ marginRight: "2.5rem" }}>
                        <RsuiteForm.Group>
                            <RsuiteForm.ControlLabel className="regional_settings__form__label">
                                {t(
                                    "tenantConfig.cultureSettingsPage.numberAndDateCulture.title"
                                )}
                                <RsuiteForm.HelpText
                                    className="regional_settings__form__help"
                                    tooltip
                                >
                                    {t(
                                        "tenantConfig.cultureSettingsPage.numberAndDateCulture.tooltip"
                                    )}
                                </RsuiteForm.HelpText>
                            </RsuiteForm.ControlLabel>
                            <RsuiteForm.Control
                                accepter={InputPicker}
                                preventOverflow={true}
                                data={numberAndDateData}
                                cleanable={false}
                                onClean={() =>
                                    handleCleanField(
                                        formFieldKeys.numberAndDate
                                    )
                                }
                                name={formFieldKeys.numberAndDate}
                                className={cn(
                                    "regional_settings__form__control",
                                    "vertically-aligned-picker"
                                )}
                            />
                        </RsuiteForm.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item
                        style={{ marginRight: "1rem", rowGap: "1.5rem" }}
                    >
                        <RsuiteForm.Group>
                            <RsuiteForm.ControlLabel className="regional_settings__form__label">
                                {t(
                                    "tenantConfig.cultureSettingsPage.numberAndDateCulture.shortFormatTitle"
                                )}
                            </RsuiteForm.ControlLabel>
                            <RsuiteForm.Control
                                name={"shortFormat"}
                                readOnly
                                value={shortDateTime}
                                className={cn(
                                    "regional_settings__form__control",
                                    "regional_settings__input--long"
                                )}
                            />
                        </RsuiteForm.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                        <RsuiteForm.Group>
                            <RsuiteForm.ControlLabel className="regional_settings__form__label">
                                {t(
                                    "tenantConfig.cultureSettingsPage.numberAndDateCulture.longFormatTitle"
                                )}
                            </RsuiteForm.ControlLabel>
                            <RsuiteForm.Control
                                name={"longFormat"}
                                readOnly
                                value={longDateTime}
                                className={cn(
                                    "regional_settings__form__control",
                                    "regional_settings__input--long"
                                )}
                            />
                        </RsuiteForm.Group>
                    </FlexboxGrid.Item>
                </FlexboxGrid>

                <FlexboxGrid
                    justify={"start"}
                    align={"bottom"}
                    style={{ marginBottom: "1.5rem", rowGap: "1.5rem" }}
                >
                    <FlexboxGrid.Item style={{ marginRight: "0.825rem" }}>
                        <RsuiteForm.Group>
                            <RsuiteForm.ControlLabel className="regional_settings__form__label">
                                {t(
                                    "tenantConfig.cultureSettingsPage.preciseScale.title"
                                )}
                                <RsuiteForm.HelpText
                                    className="regional_settings__form__help"
                                    tooltip
                                >
                                    {t(
                                        "tenantConfig.cultureSettingsPage.preciseScale.tooltip"
                                    )}
                                </RsuiteForm.HelpText>
                            </RsuiteForm.ControlLabel>
                            <RsuiteForm.Control
                                accepter={InputPicker}
                                preventOverflow={true}
                                data={preciseScaleInputPickerData}
                                cleanable={false}
                                onClean={() =>
                                    handleCleanField(formFieldKeys.preciseScale)
                                }
                                name={formFieldKeys.preciseScale}
                                className={cn(
                                    "regional_settings__form__control",
                                    "regional_settings__input--medium",
                                    "vertically-aligned-picker"
                                )}
                            />
                        </RsuiteForm.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item style={{ marginRight: "2.5rem" }}>
                        <RsuiteForm.Group>
                            <RsuiteForm.Control
                                name={"preciseScaleView"}
                                value={preciseScaleView}
                                readOnly
                                className={cn(
                                    "regional_settings__form__control",
                                    "regional_settings__input--medium"
                                )}
                            />
                        </RsuiteForm.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item style={{ marginRight: "1rem" }}>
                        <RsuiteForm.Group>
                            <RsuiteForm.ControlLabel className="regional_settings__form__label">
                                {t(
                                    "tenantConfig.cultureSettingsPage.qualityPreciseScale.title"
                                )}
                                <RsuiteForm.HelpText
                                    className="regional_settings__form__help"
                                    tooltip
                                >
                                    {t(
                                        "tenantConfig.cultureSettingsPage.qualityPreciseScale.tooltip"
                                    )}
                                </RsuiteForm.HelpText>
                            </RsuiteForm.ControlLabel>
                            <RsuiteForm.Control
                                accepter={InputPicker}
                                preventOverflow={true}
                                data={preciseQuantityData}
                                cleanable={false}
                                // Used renderValue because InputPicker treats 0 as empty value and shows the placeholder
                                renderValue={(value) => String(value)}
                                onClean={() =>
                                    handleCleanField(
                                        formFieldKeys.quantityPreciseScale
                                    )
                                }
                                name={formFieldKeys.quantityPreciseScale}
                                className={cn(
                                    "regional_settings__form__control",
                                    "regional_settings__input--medium",
                                    "vertically-aligned-picker"
                                )}
                            />
                        </RsuiteForm.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item style={{ marginRight: "2rem" }}>
                        <RsuiteForm.Group>
                            <RsuiteForm.Control
                                name={"quantityPreciseScaleView"}
                                value={quantityPreciseScaleView}
                                readOnly
                                className={cn(
                                    "regional_settings__form__control",
                                    "regional_settings__input--medium"
                                )}
                            />
                        </RsuiteForm.Group>
                    </FlexboxGrid.Item>
                </FlexboxGrid>

                <FlexboxGrid
                    justify={"start"}
                    align={"bottom"}
                    style={{ marginBottom: "1.5rem" }}
                >
                    <FlexboxGrid.Item style={{ marginRight: "1rem" }}>
                        <RsuiteForm.Group>
                            <RsuiteForm.ControlLabel className="regional_settings__form__label">
                                {t(
                                    "tenantConfig.cultureSettingsPage.currency.title"
                                )}
                            </RsuiteForm.ControlLabel>
                            <RsuiteForm.Control
                                accepter={InputPicker}
                                preventOverflow={true}
                                data={currencyData}
                                cleanable={false}
                                onClean={() =>
                                    handleCleanField(
                                        formFieldKeys.defaultCurrency
                                    )
                                }
                                name={formFieldKeys.defaultCurrency}
                                className={cn(
                                    "regional_settings__form__control",
                                    "regional_settings__input--medium",
                                    "vertically-aligned-picker"
                                )}
                            />
                        </RsuiteForm.Group>
                    </FlexboxGrid.Item>
                </FlexboxGrid>
            </RsuiteForm>
        </>
    );
};

export default Form;
